import { apiGet, apiPost, apiPatch, apiDelete } from "./client";
import type {
  Job,
  JobInput,
  JobImportRow,
  JobImportBatch,
} from "@career-ops-amplify/shared";
import { buildListQuery, type CursorListParams } from "./pagination";

export interface ListJobsResponse {
  jobs: Job[];
  total?: number;
  limit?: number;
  offset?: number;
  hasMore?: boolean;
  nextCursor?: string;
}

/** List jobs with optional filtering / cursor or offset pagination. */
export async function listJobs(params?: {
  company?: string;
  search?: string;
  status?: string;
  pipelineId?: string;
  stage?: string;
  limit?: number;
  offset?: number;
  cursor?: string;
} & CursorListParams): Promise<ListJobsResponse> {
  return apiGet<ListJobsResponse>(
    "/api/jobs",
    buildListQuery(params as Record<string, string | number | boolean | undefined | null>),
  );
}

/** Fetch a single job by ID. */
export async function getJob(jobId: string): Promise<Job> {
  return apiGet<Job>(`/api/jobs/${jobId}`);
}

/** Create a new job. */
export async function createJob(input: JobInput): Promise<Job> {
  const res = await apiPost<{ job: Job }>("/api/jobs", input);
  return res.job;
}

/** Update job fields. */
export async function updateJob(jobId: string, updates: Partial<Job>): Promise<void> {
  await apiPatch(`/api/jobs/${jobId}`, updates);
}

/** Delete a job. */
export async function deleteJob(jobId: string): Promise<void> {
  await apiDelete(`/api/jobs/${jobId}`);
}

/** Parse/fetch job description from URL. */
export async function parseJobUrl(
  jobId: string,
  mode: "ai" | "offline" = "ai",
): Promise<{ success: boolean; method?: "ai" | "offline"; description?: string; title?: string; error?: string }> {
  return apiPost(`/api/jobs/${jobId}/parse`, { mode });
}

export interface JobUrlParseFields {
  company?: string;
  role?: string;
  location?: string;
  compensation?: string;
  workMode?: string;
  payRange?: string;
  jobDescription?: string;
  postedAt?: string;
  seniority?: string;
  industry?: string;
  employmentType?: string;
  tldr?: string;
}

export interface JobUrlParseResult {
  success: boolean;
  method: "ai" | "offline" | "ats" | "unfurl";
  error?: string;
  fields: JobUrlParseFields;
  tier?: "ats" | "unfurl" | "manual";
  provider?: string;
  canonicalUrl?: string;
  resolvedUrl?: string;
  confidence?: "high" | "medium" | "low";
  hint?: string;
}

/** Parse a job URL before saving (AI by default, or offline scrape). */
export async function parseJobUrlPreview(
  url: string,
  mode: "ai" | "offline" = "ai",
): Promise<JobUrlParseResult> {
  return apiPost<JobUrlParseResult>("/api/jobs/parse-url", { url, mode });
}

/** Parse up to 25 job URLs (aligned results). Prefer offline for batch enrich. */
export async function parseJobUrlBatch(
  urls: string[],
  mode: "ai" | "offline" = "offline",
): Promise<{ results: JobUrlParseResult[]; count: number }> {
  return apiPost("/api/jobs/parse-url-batch", { urls, mode });
}

/** AI research preview before saving a job. */
export async function researchJobPreview(input: {
  url?: string;
  company: string;
  role: string;
  jobDescription?: string;
  promptKind?: string;
}): Promise<{ success: boolean; research: string; error?: string }> {
  return apiPost("/api/jobs/research-preview", input);
}

/** Batch import jobs from intake triage (CSV/JSON). */
export async function importJobs(
  jobs: JobImportRow[],
  options?: { attributionContactId?: string },
): Promise<{ jobs: Job[]; skipped: number }> {
  return apiPost("/api/jobs/import", {
    jobs,
    attributionContactId: options?.attributionContactId,
  });
}

/** Land + process a job CSV batch (S3 ops). Processing runs inline for v1. */
export async function createJobImportBatch(input: {
  csvText: string;
  fileName?: string;
  mapping: { headers: string[]; columns: Record<string, string> };
  contentType?: string;
}): Promise<{ batch: JobImportBatch }> {
  return apiPost("/api/jobs/batches", input);
}

/** Poll job import batch status. */
export async function getJobImportBatch(
  batchId: string,
): Promise<{ batch: JobImportBatch }> {
  return apiGet(`/api/jobs/batches/${batchId}`);
}

/** Presigned URL for batch artifact (original | rejects | summary). */
export async function getJobImportBatchArtifactUrl(
  batchId: string,
  kind: "original" | "rejects" | "summary",
): Promise<{ url: string; key: string; kind: string }> {
  return apiGet(`/api/jobs/batches/${batchId}/artifacts/${kind}`);
}

/** Dismiss a job from the inbox as not a fit (no pipeline required). */
export async function dismissJobNoGo(
  jobId: string,
  reason?: string,
): Promise<Job> {
  const res = await apiPost<{ job: Job }>(`/api/jobs/${jobId}/no-go`, { reason });
  return res.job;
}

export interface BulkNoGoResult {
  message: string;
  ok: string[];
  skippedPiped: string[];
  missing: string[];
  failed: Array<{ jobId: string; error: string }>;
  counts: {
    ok: number;
    skippedPiped: number;
    missing: number;
    failed: number;
  };
}

/** Bulk dismiss inbox jobs; skips jobs that already have a pipeline. */
export async function dismissJobsNoGoBulk(
  jobIds: string[],
  reason?: string,
): Promise<BulkNoGoResult> {
  return apiPost<BulkNoGoResult>("/api/jobs/no-go/bulk", { jobIds, reason });
}

export type BulkRestoreResult = BulkNoGoResult;

/** Bulk restore no-go jobs to inbox; skips piped jobs. */
export async function restoreJobsToInboxBulk(
  jobIds: string[],
): Promise<BulkRestoreResult> {
  return apiPost<BulkRestoreResult>("/api/jobs/restore-inbox/bulk", { jobIds });
}

/** Machine-readable Jobs / Pipeline bucket rules. */
export async function getJobBucketRules(): Promise<Record<string, string>> {
  return apiGet("/api/jobs/bucket-rules");
}

/** Restore a no-go job back to the inbox. */
export async function restoreJobToInbox(jobId: string): Promise<Job> {
  const res = await apiPost<{ job: Job }>(`/api/jobs/${jobId}/restore-inbox`, {});
  return res.job;
}

/** Trigger AI research on a job. */
export async function researchJob(
  jobId: string,
  input?: { promptKind?: string },
): Promise<{ success: boolean; research?: string; error?: string }> {
  return apiPost(`/api/jobs/${jobId}/research`, input ?? {});
}